import React from 'react';
import { useImportHistoryQuery } from '@/api';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { ImportHistoryEntry } from './importHistoryMeta';

/**
 * Consumer contract for the `ImportHistory` widget. Wraps
 * `useImportHistoryQuery` (`/purchases/import-history`) in the widget's
 * `{ data, error, isLoading, onRetry }` shape. History is read-only, so there
 * is no per-row action adapter.
 */
export const useImportHistory = () => {
    const query = useImportHistoryQuery();

    // Keep the last successful page so a failed refetch doesn't wipe visible
    // rows (see usePendingInvoices).
    const lastItemsRef = React.useRef<ImportHistoryEntry[]>([]);
    if (query.data?.success) {
        lastItemsRef.current = query.data.response.items;
    }

    const data = React.useMemo<ImportHistoryEntry[]>(
        () =>
            query.data?.success
                ? query.data.response.items
                : lastItemsRef.current,
        [query.data],
    );

    // Gate the error while a retry is in flight (see usePendingInvoices) so
    // the boundary's Retry doesn't re-throw the stale failed envelope.
    const error = React.useMemo<ErrorResponse | null>(
        () =>
            !query.isFetching && query.data && !query.data.success
                ? (query.data.error ?? null)
                : null,
        [query.isFetching, query.data],
    );

    const onRetry = React.useCallback(() => {
        void query.refetch();
    }, [query]);

    return {
        data,
        error,
        isLoading:
            query.isPending || (query.isFetching && !query.data?.success),
        onRetry,
    } as const;
};
